import { useAuthStore } from "~/store/auth-store";
import { getRouteAccessForPath } from "~/code/page-routes";
import ApiManager from "~/code/api-manager";
import { useRolePermission } from "~/composables/useRolePermission";
import { useUserStore } from "~/store/user-store";

export default defineNuxtRouteMiddleware(async (to) => {
  if (to.path === "/") {
    return;
  }

  const authenticated =
    localStorage.getItem("authenticated") === "true" ||
    sessionStorage.getItem("authenticated") === "true";

  // check API Manager for token
  const apiManager = ApiManager.getInstance();
  // get token from local storage or session storage
  const bearerToken =
    localStorage.getItem("bearerToken") || sessionStorage.getItem("bearerToken");
  // auth pages like login or reset password are always accessible
  if (to.path.startsWith("/auth")) {
    return;
  }
  if (!bearerToken) {
    console.log("No token found in local storage.");
    return "/auth";
  }
  // make sure that the API Manager has the token
  const apiToken = apiManager.updateAccessToken(bearerToken);

  let user = null;
  const userItem =
    localStorage.getItem("user") || sessionStorage.getItem("user");

  try {
    if (userItem && userItem !== "undefined") {
      user = JSON.parse(userItem);
    }
  } catch (e) {
    user = null;
  }

  const impersonatedRole =
    (localStorage.getItem("auth-impersonated-role") || "").trim();

  if (impersonatedRole.length > 0 && impersonatedRole.toLowerCase() !== "admin") {
    const userStore = useUserStore();

    if (userStore.roles.length === 0) {
      await userStore.fetchRoles(1, 500, "name", "asc", null);
    }
  }

  if (user?.permissions && Array.isArray(user.permissions)) {
    const hasNumericPermissions = user.permissions.some((permission) => {
      return (
        typeof permission === "number" ||
        (typeof permission === "string" && /^\d+$/.test(permission.trim()))
      );
    });

    if (hasNumericPermissions) {
      const userStore = useUserStore();
      await userStore.ensurePermissionsLoaded();

      user.permissions = user.permissions
        .map((permission) => {
          if (typeof permission === "number") {
            return userStore.permissionNameById(permission);
          }

          if (typeof permission === "string") {
            const trimmed = permission.trim();

            if (/^\d+$/.test(trimmed)) {
              return userStore.permissionNameById(parseInt(trimmed, 10));
            }

            return trimmed;
          }

          return null;
        })
        .filter((permission): permission is string =>
          Boolean(permission && permission.trim().length > 0)
        );

      localStorage.setItem("user", JSON.stringify(user));
    }
  }
  // Check if password reset is needed
  // They may not do anything if their password needs to be reset
  if (authenticated && user?.needsPasswordReset) {
    console.log("User needs to reset password.");
    var token = to.query.token;
    var email = to.query.email;
    if (email === undefined || email === null || email === "") {
      email = user.email;
    }

    return "/auth/resetpassword/?email=" + email;
  }

  // Check if authorized
  if (
    !authenticated ||
    authenticated === null ||
    (authenticated && authenticated === false) ||
    !user ||
    user === null
  ) {
    console.log("Redirecting to login page");
    return "/auth";
  }
  const routeAccess = getRouteAccessForPath(to?.path);

  // No route-level restriction found.
  if (!routeAccess) {
    return true;
  }

  const { computeRoles, computePermissions, computeRolesAndPermissions } =
    useRolePermission();

  const routeRoles = routeAccess.roles;
  const routePermissions = routeAccess.permissions;

  // If route has no restrictions, allow access
  if (!routeRoles && !routePermissions) {
    return true;
  }
  // Route has only permissions requirement
  else if (!routeRoles && routePermissions) {
    if (!computePermissions(routePermissions)) {
      console.log(
        `${to.path} is a protected route but your permission is not authorized.`
      );
      return "/unauthorized";
    }
  }
  // Route has only roles requirement
  else if (routeRoles && !routePermissions) {
    if (!computeRoles(routeRoles)) {
      console.log(
        `${to.path} is a protected route but your role is not authorized.`
      );
      return "/unauthorized";
    }
  }
  // Route has both roles AND permissions - use OR logic (same as sidebar)
  // User needs EITHER the required role OR the required permission
  else {
    if (
      !computeRolesAndPermissions(
        routeRoles || [],
        routePermissions || []
      )
    ) {
      console.log(
        `${to.path} is a protected route but you don't have the required role or permission.`
      );
      return "/unauthorized";
    }
  }

  console.log(to.path + " is a protected route, but you are logged in.");
});
